import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import moment from "moment";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup, showSnackbar } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";

import { extendContractSchema, ExtendContractSchemaInput, ExtendContractSchemaOutput } from "view/contractsNew/utils/contract.schema";

import { ExtendContractsDocument } from "__generated__/view/contractsNew/graphql/contract.mutations.generated";

type ContractExtendDialogProps = {
	open: boolean,
	onClose: () => void,
	contracts: { id: number; validUntil?: number | null; contractOptions?: { validFrom: number }[] | null }[],
	refetch: () => void,
};

const ContractExtendDialog = ({
	open,
	onClose,
	contracts,
	refetch,
}: ContractExtendDialogProps) => {

	const defaultValues = {
		validUntil: null,
	};

	const { control, handleSubmit, reset } = useForm<ExtendContractSchemaInput, unknown, ExtendContractSchemaOutput>({
		resolver: zodResolver(extendContractSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const [ extendContract, { loading } ] = useMutation(ExtendContractsDocument);

	const save = (data: ExtendContractSchemaOutput) => {
		extendContract({
			variables: {
				contractIds: contracts.map(c => c.id),
				validUntil: data.validUntil?.valueOf(),
			},
		}).then(() => {
			handleOnClose();
			showSnackbar(intl.formatMessage({ id: "SUCCESSFUL_SAVED" }), { variant: "success" });
		}).finally(() => {
			refetch();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "EXTEND_CONTRACTS" })}
			form="extendContractForm"
			loading={loading}>
			<form onSubmit={(validity) => void handleSubmit(save)(validity)} id="extendContractForm">
				<FormGroup
					label={intl.formatMessage({ id: "VALID_UNTIL" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="validUntil"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker
									{...field}
									minDate={new Date(Math.max(...contracts.map(contract => contract.validUntil ?? 0)))}
									maxDate={new Date(Math.min(
										...contracts.flatMap(contract => contract.contractOptions ?? [])
											.map(option => option.validFrom),
									))}
									onChange={date => field.onChange(date ? moment(date).endOf("day").unix() * 1000 : null)}
								/>
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default ContractExtendDialog;
